﻿import { currentVersion } from "../../VersionProvider";
import { getSettings } from "../settingsProvider";
import { isNullOrWhitespace } from "../stringOperations";
import { Result } from "./result";
import { jwtDecode } from "jwt-decode";

let _token: string;
let _externalToken: string;
// Kept apart from _token because setJwtToken("") forces a refetch but must not release the tab from its customer
let _tabCustomerId: string | undefined;

export function setJwtToken(token: string) {
    _token = token;
}

export function getJwtToken() {
    return _token;
}

// localStorage is shared by every tab, so it only decides the customer until this tab has fetched its first JWT
export function getTabCustomerId() {
    return _tabCustomerId ?? localStorage.getItem("customerId");
}

export function setExternalToken(externalToken: string) {
    _externalToken = externalToken;
}

export function getExternalToken() {
    return _externalToken;
}

export async function post<T>(url: string, body: any): Promise<Result<T>> {
    const response = await doFetch(url, "POST", body);
    return await handleResponse(response);
}

export async function postGet<T>(url: string, body: any): Promise<T> {
    const response = await doFetch(url, "POST", body);
    return await response.json();
}

export async function get<T>(url: string): Promise<T> {
    const response = await doFetch(url);
    return await response.json();
}

export async function getRaw(url: string, method?: string, body?: any, signal?: AbortSignal): Promise<Response> {
    return await doFetch(url, method, body, true, signal);
}

export async function putNoBody<T>(url: string): Promise<Result<T>> {
    return await put(url, undefined);
}

export async function put<T>(url: string, body: any): Promise<Result<T>> {
    const response = await doFetch(url, "PUT", body);
    return await handleResponse(response);
}

export async function deleteHttp<T>(url: string): Promise<Result<T>> {
    const response = await doFetch(url, "DELETE");
    return await handleResponse(response);
}

// Uploads the file on its own first, then sends the save model as JSON with the id of the upload. The backend only turns
// the upload into a document when the save claims it, so an upload that is never saved stays unreachable.
export async function sendWithFile<T>(
    method: "POST" | "PUT",
    url: string,
    model: object,
    file: File | undefined,
    onProgress: (percentage: number) => void
): Promise<Result<T>> {
    const body = file ? { ...model, uploadId: await uploadFile(file, onProgress) } : model;
    return method === "POST" ? await post<T>(url, body) : await put<T>(url, body);
}

// The upload alone, for save models that carry the upload id deeper than the top level. This is the only place that
// knows uploads go through XMLHttpRequest (fetch cannot report upload progress).
export async function uploadFile(file: File, onProgress: (percentage: number) => void): Promise<string> {
    const response = await doXmlHttpRequest(`/documents/uploads?fileName=${encodeURIComponent(file.name)}`, "POST", file, onProgress);
    if (!response.ok) {
        throw new Error(`Error uploading file. Status code: ${response.status}`);
    }
    const upload: { uploadId: string } = await response.json();
    return upload.uploadId;
}

async function doXmlHttpRequest(
    url: string,
    method: string,
    body: XMLHttpRequestBodyInit,
    onProgress: (percentage: number) => void,
    requestJwtOnUnauthorized: boolean = true
): Promise<Response> {
    const fullUrl = getFullUrl(url);
    const { response, responseUrl } = await sendXmlHttpRequest(fullUrl, method, body, createRequestHeaders(), onProgress);

    return await verifyResponse(fullUrl, response, responseUrl, requestJwtOnUnauthorized, () => doXmlHttpRequest(url, method, body, onProgress, false));
}

function sendXmlHttpRequest(
    fullUrl: string,
    method: string,
    body: XMLHttpRequestBodyInit,
    headers: Headers,
    onProgress: (percentage: number) => void
): Promise<{ response: Response; responseUrl: string }> {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open(method, fullUrl);
        request.responseType = "blob";
        headers.forEach((value, name) => request.setRequestHeader(name, value));

        request.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                onProgress(Math.round((event.loaded / event.total) * 100));
            }
        };
        request.onload = () => resolve({ response: toResponse(request), responseUrl: request.responseURL });
        // fetch rejects with a TypeError when the request never gets a response
        request.onerror = () => reject(new TypeError(`Network error calling API. URL: ${fullUrl}`));
        request.onabort = () => reject(new TypeError(`Request aborted calling API. URL: ${fullUrl}`));

        request.send(body);
    });
}

function toResponse(request: XMLHttpRequest) {
    // The Response constructor throws when a null body status is given a body, even an empty one
    const statusHasNoBody = [204, 205, 304].includes(request.status);
    return new Response(statusHasNoBody ? null : request.response, {
        status: request.status,
        statusText: request.statusText,
        headers: parseResponseHeaders(request.getAllResponseHeaders()),
    });
}

function parseResponseHeaders(rawHeaders: string) {
    const headers = new Headers();
    for (const line of rawHeaders.trim().split(/[\r\n]+/)) {
        const separatorIndex = line.indexOf(":");
        if (separatorIndex > 0) {
            headers.append(line.substring(0, separatorIndex).trim(), line.substring(separatorIndex + 1).trim());
        }
    }
    return headers;
}

function createRequestHeaders() {
    const headers = new Headers();

    headers.set("Authorization", "Bearer " + getJwt());
    headers.set("pragma", "no-cache");
    headers.set("cache-control", "no-cache");

    return headers;
}

function getFullUrl(url: string) {
    return url.startsWith("http") ? url : getSettings().apiBaseUrl + url;
}

async function doFetch(url: string, method?: string, body?: any, requestJwtOnUnauthorized: boolean = true, signal?: AbortSignal): Promise<Response> {
    const headers = createRequestHeaders();

    if (method === "POST" || method === "PUT") {
        headers.set("Content-Type", "application/json");
    }
    const fullUrl = getFullUrl(url);

    const response = await fetch(fullUrl, { method: method, body: JSON.stringify(body), headers: headers, signal: signal });

    return await verifyResponse(fullUrl, response, response.url, requestJwtOnUnauthorized, () => doFetch(url, method, body, false, signal));
}

async function verifyResponse(
    fullUrl: string,
    response: Response,
    responseUrl: string,
    requestJwtOnUnauthorized: boolean,
    retryWithNewJwt: () => Promise<Response>
): Promise<Response> {
    const apiBaseUrl = getSettings().apiBaseUrl;
    if (fullUrl.startsWith(apiBaseUrl)) {
        const backendVersion = response.headers.get("releaseVersion");
        if (isNullOrWhitespace(backendVersion ?? undefined)) {
            throw new Error("Could not get releaseVersion from response header (apiShared)");
        }
        if (currentVersion !== backendVersion) {
            // eslint-disable-next-line no-console
            console.log(`Current version: ${currentVersion} - Backend version: ${backendVersion}`);
            window.location.reload();
        }
    }

    if (response.status === 403) {
        window.location.href = "/privacy/forbidden";
    }

    if (response.status === 404) {
        window.location.href = "/privacy/resource-not-found";
    }

    if (response.status === 401) {
        if (requestJwtOnUnauthorized) {
            await getAndSaveJwt();
            return await retryWithNewJwt();
        } else {
            throw new Error("Could not fetch JWT");
        }
    }

    if (!isOkOrClientErrorStatus(response.status)) {
        throw new Error(`Error calling API. Status code: ${response.status}. URL: ${responseUrl}`);
    }

    sessionStorage.removeItem("challengeIdp");
    return response;
}

function isOkOrClientErrorStatus(status: number) {
    return (status >= 200 && status < 300) || (status >= 400 && status < 500);
}

export async function getAndSaveJwt() {
    const customerId = getTabCustomerId();
    const language = localStorage.getItem("language");

    const response = await fetch(getSettings().jwtTokenUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            externalToken: _externalToken,
            customerId: customerId,
            language: language,
        }),
        redirect: "manual",
        credentials: "include",
    });

    if (response.type === "opaqueredirect") {
        window.location.href = response.url;
    } else if (response.status === 401) {
        if (sessionStorage.getItem("challengeIdp") === "true") {
            // To prevent page refresh loop
            throw new Error("JWT could not be validated");
        } else {
            sessionStorage.setItem("challengeIdp", "true");
            // Makes the AuthProvider challenge the IDP
            // eslint-disable-next-line no-console
            console.log("Could not retrieve new Privacy JWT. External token is probably expired. Refreshing page...");
            window.location.reload();
        }
    } else if (response.status === 400) {
        let reason = await response.text();
        if (reason === "usernotfound") window.location.href = "/privacy/user-not-found";
        else if (reason === "inactivecustomer") window.location.href = "/privacy/account-closed";
        else if (reason === "inactiveuser") {
            window.location.href = "/privacy/inactive-user";
        } else throw new Error("Unknown reason: " + reason);
    } else {
        let token = await response.text();
        bindTabToCustomerOf(token);
        setJwtToken(token);
    }
}

// Retrying a request with another customer's JWT would silently read and write that customer's data from this tab
function bindTabToCustomerOf(token: string) {
    const tokenCustomerId = jwtDecode<{ CustomerId: string }>(token).CustomerId;

    if (_tabCustomerId === undefined) {
        _tabCustomerId = tokenCustomerId;
    } else if (_tabCustomerId !== tokenCustomerId) {
        throw new Error(`Refused a JWT for customer ${tokenCustomerId} in a tab bound to customer ${_tabCustomerId}`);
    }
}

function getJwt() {
    return getJwtToken();
}

async function handleResponse<T>(response: Response): Promise<Result<T>> {
    const responseBody = await response.text();

    if (response.status === 400) {
        if (responseBody) {
            const errorData = JSON.parse(responseBody);
            return new Result<T>(undefined, errorData);
        } else {
            return new Result<T>(undefined, ["error"]);
        }
    }

    if (responseBody) {
        const data = JSON.parse(responseBody) as T;
        return new Result<T>(data);
    }

    return new Result<T>();
}
